// Deals — web port of the mobile DealsScreen. Two sections: claimable coupons
// (claim → lands in Wallet) and the live flyer deals feed with computed savings
// badges. Search + store filter mirror the mobile filter sheet (simplified to
// a web-native inline toolbar). Token-styled for light + dark.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Tag, Check, Store, Clock } from 'lucide-react';
import {
  fetchLiveDeals, listCoupons, claimCoupon, dealBadge, parsePrice,
} from '../services/dealsService';
import { discountLabel } from '../services/walletService';
import PromotionalBanner from './PromotionalBanner';
import './DealsScreen.css';

function DealsScreen() {
  const [coupons, setCoupons] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [storeFilter, setStoreFilter] = useState(null);
  const [claimingId, setClaimingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [c, d] = await Promise.allSettled([listCoupons(), fetchLiveDeals()]);
    if (c.status === 'fulfilled') setCoupons(c.value);
    if (d.status === 'fulfilled') setDeals(d.value);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const stores = useMemo(() => {
    const counts = {};
    deals.forEach((d) => { if (d.store) counts[d.store] = (counts[d.store] || 0) + 1; });
    coupons.forEach((c) => { if (c.store_name) counts[c.store_name] = (counts[c.store_name] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [deals, coupons]);

  const q = query.trim().toLowerCase();
  const matchStore = (s) => !storeFilter || (s || '').toLowerCase() === storeFilter.toLowerCase();
  const matchQuery = (...fields) => !q || fields.some((f) => (f || '').toLowerCase().includes(q));

  const shownCoupons = coupons.filter((c) =>
    matchStore(c.store_name) && matchQuery(c.title, c.description, c.store_name));
  const shownDeals = deals.filter((d) =>
    matchStore(d.store) && matchQuery(d.item_name, d.store, d.section));

  const claim = async (c) => {
    setClaimingId(c.id);
    try {
      await claimCoupon(c.id);
      setCoupons((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_claimed: true } : x)));
    } catch (err) {
      if (err.response?.status === 409) {
        setCoupons((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_claimed: true } : x)));
      } else {
        window.alert('Could not claim this coupon. Please try again.');
      }
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <div className="deals-screen">
      <div className="deals-inner">
        <PromotionalBanner />
        <header className="deals-head">
          <div>
            <p className="deals-sub">Claim coupons and browse this week's local deals</p>
          </div>
        </header>

        <div className="deals-toolbar">
          <div className="deals-search">
            <Search size={16} />
            <input
              value={query}
              placeholder="Search deals, items, stores…"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {stores.length > 0 && (
            <div className="deals-store-chips">
              <button
                className={`deals-store-chip${!storeFilter ? ' active' : ''}`}
                onClick={() => setStoreFilter(null)}
              >All</button>
              {stores.map(([name, count]) => (
                <button
                  key={name}
                  className={`deals-store-chip${storeFilter === name ? ' active' : ''}`}
                  onClick={() => setStoreFilter(storeFilter === name ? null : name)}
                >
                  <Store size={12} /> {name} <span className="deals-store-count">{count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="deals-empty">Loading deals…</div>
        ) : (
          <>
            {/* Coupons */}
            <section className="deals-section">
              <h2 className="deals-section-title"><Tag size={16} /> Coupons</h2>
              {shownCoupons.length === 0 ? (
                <div className="deals-empty small">No coupons match your filters.</div>
              ) : (
                <div className="coupon-grid">
                  {shownCoupons.map((c) => {
                    const expired = c.days_left <= 0;
                    return (
                      <article key={c.id} className={`coupon-card${expired ? ' expired' : ''}`}>
                        <div className="coupon-top">
                          <span className="coupon-badge">{discountLabel(c)}</span>
                          <span className="coupon-store">{c.store_name}</span>
                        </div>
                        <h3 className="coupon-title">{c.title}</h3>
                        {c.description && <p className="coupon-desc">{c.description}</p>}
                        <div className="coupon-foot">
                          <span className="coupon-expiry">
                            <Clock size={13} /> {expired ? 'Expired' : `${c.days_left}d left`}
                          </span>
                          {c.is_claimed ? (
                            <span className="coupon-inwallet"><Check size={14} /> In wallet</span>
                          ) : (
                            <button
                              className="coupon-claim"
                              disabled={expired || claimingId === c.id}
                              onClick={() => claim(c)}
                            >
                              {claimingId === c.id ? 'Claiming…' : 'Claim'}
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Live deals */}
            <section className="deals-section">
              <h2 className="deals-section-title"><Store size={16} /> This week's deals</h2>
              {shownDeals.length === 0 ? (
                <div className="deals-empty small">No deals match your filters.</div>
              ) : (
                <div className="deal-list">
                  {shownDeals.map((d) => {
                    const badge = dealBadge(d);
                    const orig = parsePrice(d.original_price);
                    return (
                      <article key={d.id} className="deal-card">
                        <div className="deal-main">
                          <div className="deal-name-row">
                            <span className="deal-name">{d.item_name}</span>
                            {badge && <span className={`deal-badge ${badge.tone}`}>{badge.label}</span>}
                          </div>
                          <div className="deal-meta">
                            <span>{d.store}</span>
                            {d.section && <><span className="deal-dot">·</span><span>{d.section}</span></>}
                            {d.valid_until && <><span className="deal-dot">·</span><span>until {d.valid_until}</span></>}
                          </div>
                        </div>
                        <div className="deal-price-col">
                          <span className="deal-price">{d.price}</span>
                          {Number.isFinite(orig) && <span className="deal-orig">{d.original_price}</span>}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default DealsScreen;
